import connect from 'components/Shared/connect';
import { useRef, useCallback, useEffect, useMemo } from 'react';
import { delay, NO_ELEMENT } from 'utils';
import { IMenuItem } from 'models/IMenuItem';
import { closeMainMenu, isPageLoading, openMainMenu } from 'components/MainMenu/actions';
import styles from './styles.scss';
import { Section } from 'components/MenuSection/themes/BGH/section';
import { selectSections, selectOurBrands } from 'components/MainMenu/selectors';
import { useMenuToggle } from 'components/MainMenu/useMenuToggle';
import { NotifyLink } from 'components/Shared/NotifyLink';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { getChannel } from 'views/ProductPage/selector';
import { isArredoChannel } from 'components/Shared/channel';
import { GtmHeaderNavClick } from 'store/googleTagManager/actions';

const OurBrands = () => {
  const { title, url, ticket } = useSelector(selectOurBrands);
  const reduxDispatch = useDispatch();

  const handleOnClick = () => {
    reduxDispatch(GtmHeaderNavClick(url));
  };

  // Title is 'Våra varumärken' but we only want 'varumärken'
  const trimmedTitle = useMemo(() => {
    return title.split(' ')[1];
  }, [title]);

  if (title === '') {
    return NO_ELEMENT;
  }

  return (
    <div className={cn(styles.section, styles.brands)}>
      <NotifyLink onClick={handleOnClick} className={styles.sectionLink} ticket={ticket} url={url}>
        <span className={styles.sectionTitle}>{trimmedTitle}</span>
      </NotifyLink>
    </div>
  );
};

interface MergedProps {
  sections: IMenuItem[];
  isLoading: boolean;
  isOpen: boolean;
  openMainMenu: () => void;
  closeMainMenu: () => void;
}

const DesktopMenu = ({ sections, isOpen, isLoading, openMainMenu, closeMainMenu }: MergedProps) => {
  const delayRef = useRef(delay());
  useEffect(() => delayRef.current.cleanup, []);
  const setMenuOpen = useCallback(
    (open: boolean) => delayRef.current.setDelay(() => (open ? openMainMenu() : closeMainMenu())),
    [openMainMenu, closeMainMenu]
  );
  const onMouseOver = () => setMenuOpen(true);
  const onMouseOut = () => setMenuOpen(false);
  const [openId, open, close] = useMenuToggle<string>(isOpen, isLoading);

  const centerMenu = isArredoChannel(useSelector(getChannel));

  return (
    <nav
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onTouchStart={onMouseOver}
      className={cn(styles.menu, { [styles.centerMenu]: centerMenu })}
    >
      {sections.map(section => (
        <Section
          openMenu={open(section.id)}
          closeMenu={close}
          key={section.id}
          isLoading={isLoading}
          isOpen={section.id === openId && isOpen}
          {...section}
        />
      ))}
      <OurBrands />
    </nav>
  );
};

export default connect<MergedProps>(
  state => ({
    sections: selectSections(state),
    isLoading: isPageLoading(state.currentPage),
    isOpen: state.header.mainMenu.isOpen
  }),
  dispatch => ({
    openMainMenu: () => dispatch(openMainMenu()),
    closeMainMenu: () => dispatch(closeMainMenu())
  })
)(DesktopMenu);
